import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";
import { normalizeErrorString } from "@medplum/core";
import { captureException } from "@sentry/react";
import { z } from "zod";

import { ROUTES } from "@/constants/routes.constants";
import { setPassword } from "@/apis/medplum";

import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PasswordInput,
} from "@/components/base";

const createFormSchema = (t: (key: string) => string) =>
  z
    .object({
      password: z.string().min(8),
      repeatPassword: z.string().min(1),
    })
    .refine(({ password, repeatPassword }) => password === repeatPassword, {
      path: ["repeatPassword"],
      message: t("Passwords do not match"),
    });

type FormValues = {
  password: string;
  repeatPassword: string;
};

export const SetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /**
   * Captured once: the effect below scrubs the secret from the URL, so a
   * later read of `searchParams` would come back empty.
   */
  const [{ id, secret }] = useState(() => ({
    id: searchParams.get("id"),
    secret: searchParams.get("secret"),
  }));

  useEffect(() => {
    if (!id || !secret) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }

    navigate(ROUTES.SET_PASSWORD, { replace: true });
  }, [navigate, id, secret]);

  const form = useForm<FormValues>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: { password: "", repeatPassword: "" },
  });

  const handleSubmit = async ({ password }: FormValues) => {
    try {
      await setPassword({
        id: id ?? "",
        secret: secret ?? "",
        password,
      });
      toast.success(t("Password set successfully. You can now sign in."));
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (error) {
      captureException(error, { extra: { id } });
      toast.error(normalizeErrorString(error));
    }
  };

  return (
    <Card
      padding="lg"
      className="flex w-full max-w-md -translate-y-1/6 flex-col gap-6"
    >
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-3xl font-semibold">{t("Set password")}</h1>
        <p className="text-muted-foreground text-center text-sm">
          {t("Choose a password for your account.")}
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit(handleSubmit)(event);
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Password")}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="repeatPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Repeat Password")}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            {t("Set Password")}
          </Button>
        </form>
      </Form>
      <Button
        className="-mt-4"
        variant="tertiary"
        onClick={() => navigate(ROUTES.LOGIN)}
      >
        {t("Back")}
      </Button>
    </Card>
  );
};
